import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'
import { LegacyThemeType, LEGACY_THEME_NAMES } from '../../themes/legacyThemes'
import { SwashbuckleProvider } from '../SwashbuckleProvider/SwashbuckleProvider'

import { themes } from '../../themes'
import { AnalyticsClient } from '../../../utils/analytics/analyticsAdapter'

interface LegacyThemeProviderProps {
  theme?: LegacyThemeType
  children: JSX.Element
  analyticsClient?: AnalyticsClient
}

const emptyTheme: LegacyThemeType = {
  name: undefined,
  tokens: {},
  isLightThemeType: false,
  isLightNeutral: false,
}

const LegacyThemeContext = createContext<{
  theme: LegacyThemeType
  setTheme: React.Dispatch<React.SetStateAction<LegacyThemeType>>
}>({
  theme: emptyTheme,
  setTheme: () => null,
})

const oldToNewThemeMapping = oldThemeName => {
  if (typeof oldThemeName === 'undefined') {
    return {}
  }

  if (
    oldThemeName === LEGACY_THEME_NAMES.HB_DEFAULT ||
    oldThemeName === LEGACY_THEME_NAMES.HB_DEFAULT_NEUTRAL
  ) {
    return themes.dark
  } else if (oldThemeName === LEGACY_THEME_NAMES.LIGHT_NEUTRAL) {
    return themes.poweredBy
  }

  return themes.light
}

// References to theme in this function refer to exports from
// src/lib/themes/legacyThemes.ts
export const LegacyThemeProvider = ({
  children,
  theme: initialTheme = emptyTheme,
  analyticsClient,
}: LegacyThemeProviderProps) => {
  const [theme, setTheme] = useState(initialTheme)

  useLayoutEffect(() => {
    const { tokens } = theme

    Object.keys(tokens).forEach(token => {
      document.documentElement.style.setProperty(`--${token}`, tokens[token])
    })

    // When the theme is changed, remove the cache key set by Chakra. Otherwise,
    // Chakra will be out of sync with our theme
    window.localStorage.removeItem('chakra-ui-color-mode')

    return () => {
      Object.keys(tokens).forEach(token => {
        document.documentElement.style.removeProperty(`--${token}`)
      })
    }
  }, [theme.name])

  useLayoutEffect(() => {
    setTheme(initialTheme || emptyTheme)
  }, [initialTheme.name])

  return (
    <LegacyThemeContext.Provider
      value={{
        setTheme,
        theme,
      }}
    >
      <SwashbuckleProvider
        theme={oldToNewThemeMapping(theme.name)}
        prepend={true}
        analyticsClient={analyticsClient}
      >
        {children}
      </SwashbuckleProvider>
    </LegacyThemeContext.Provider>
  )
}

export const useTheme = () => {
  return useContext(LegacyThemeContext)
}
